// "Save & next pending" on a delivery's Paltrack card (UF-09, F01 gate intake).
//
// Filling in Paltrack is a worklist the clerk runs ~30 times a day. Without a
// "next", every delivery ended at Back → the board's top → re-find your place.
// These two reads answer "which delivery is next" and "how many are left" with
// the SAME rule the board's Paltrack pending chip uses (deliveriesFilter.ts
// matchesStatus + Deliveries.tsx's chip count): paltrack_status = 'pending',
// not a DRAFT, not deleted, inside the board's server window, in the board's
// order (received_at newest first).
//
// "Next" is the next one DOWN the board from the delivery just saved (older
// received_at); past the bottom it wraps to the newest one still pending. The
// delivery just saved is always excluded, whatever its status now reads.
//
// The order is (received_at, delivery_id) newest first, and "down" is the
// keyset on BOTH columns — a gate clerk captures a wagon's loads in the same
// minute, so received_at alone ties, and a tie read by `received_at <` alone
// skips every delivery sharing the saved one's timestamp (and the wrap-around
// then bounces between them). delivery_id breaks the tie: a total order.
//
// Both reads THROW on failure — a failed read is not "none left", and the
// caller must say so rather than open a wrong delivery or claim the list is
// done.
import type { supabase as Supabase } from '@/lib/supabase'
import { countOrThrow } from '@/lib/readState'

export interface PendingPaltrackScope {
  tenantId:          string
  /** The delivery on screen — never offered as its own "next". */
  currentId:         string
  currentReceivedAt: string
  /** The board's server window (useHistoryWindow): null = whole history. */
  windowFromIso:     string | null
  /** D6: opened from a Weekly row. Scopes next and "(N left)" to this ISO week and drops the window. */
  week?:             { year: number; week: number } | null
}

type Db = typeof Supabase

function pendingOthers(db: Db, s: PendingPaltrackScope, head = false) {
  let q = db
    .from('deliveries')
    .select('delivery_id', head ? { count: 'exact', head: true } : undefined)
    .eq('tenant_id', s.tenantId)
    .eq('paltrack_status', 'pending')
    .neq('status', 'DRAFT')
    .is('deleted_at', null)
    .neq('delivery_id', s.currentId)
  if (s.week) q = q.eq('iso_year_number', s.week.year).eq('iso_week_number', s.week.week)
  else if (s.windowFromIso) q = q.gte('received_at', s.windowFromIso)
  return q
}

/** How many OTHER deliveries are still pending Paltrack (the "(8 left)"). */
export async function countOtherPendingPaltrack(db: Db, s: PendingPaltrackScope): Promise<number> {
  return countOrThrow(await pendingOthers(db, s, true))
}

/** The next delivery pending Paltrack in the board's order, or null when none is left. */
export async function nextPendingPaltrackId(db: Db, s: PendingPaltrackScope): Promise<string | null> {
  // Values are double-quoted: a timestamp carries `.` and `:`, which PostgREST's
  // logic-tree syntax reserves.
  const at = `"${s.currentReceivedAt}"`
  const down = await pendingOthers(db, s)
    .or(`received_at.lt.${at},and(received_at.eq.${at},delivery_id.lt."${s.currentId}")`)
    .order('received_at', { ascending: false })
    .order('delivery_id', { ascending: false })
    .limit(1)
  if (down.error) throw down.error
  const below = down.data?.[0]
  if (below) return below.delivery_id

  const top = await pendingOthers(db, s)
    .order('received_at', { ascending: false })
    .order('delivery_id', { ascending: false })
    .limit(1)
  if (top.error) throw top.error
  return top.data?.[0]?.delivery_id ?? null
}

// D3: ONE window for the board's Paltrack chip and the page's "(N left)".
// The board stamps the window it is showing into the router state of the link
// it opens ('all' = whole history); a page opened with no state uses the
// default 90 days. The History toggle itself stays a per-visit control.
const WINDOW_KEY = 'paltrackWindow'

/** Router state for a link that opens a delivery from the board. */
export function paltrackWindowState(windowFromIso: string | null): { paltrackWindow: string } {
  return { [WINDOW_KEY]: windowFromIso ?? 'all' }
}

/** The stamped window: an ISO string, null = whole history, undefined = no stamp. */
export function readPaltrackWindow(state: unknown): string | null | undefined {
  const raw = (state as { paltrackWindow?: unknown } | null)?.paltrackWindow
  if (raw === 'all') return null
  if (typeof raw === 'string' && !Number.isNaN(Date.parse(raw))) return raw
  return undefined
}

/** Pending Paltrack deliveries OLDER than the window: what the chip cannot count. */
export async function countOlderPendingPaltrack(db: Db, tenantId: string, windowFromIso: string): Promise<number> {
  return countOrThrow(await db
    .from('deliveries')
    .select('delivery_id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('paltrack_status', 'pending')
    .neq('status', 'DRAFT')
    .is('deleted_at', null)
    .lt('received_at', windowFromIso))
}

// D6: the Weekly page stamps the week it is reconciling into the link it opens;
// "next" and "(N left)" then stay inside that ISO week (a week older than the
// window is still reachable). A page opened with no stamp follows the board.
/** Router state for a link that opens a delivery from a Weekly row. */
export function paltrackWeekState(week: { year: number; week: number }): { paltrackWeek: { year: number; week: number } } {
  return { paltrackWeek: week }
}

/** The stamped week, or undefined when absent or malformed. */
export function readPaltrackWeek(state: unknown): { year: number; week: number } | undefined {
  const raw = (state as { paltrackWeek?: { year?: unknown; week?: unknown } } | null)?.paltrackWeek
  return typeof raw?.year === 'number' && typeof raw.week === 'number' ? { year: raw.year, week: raw.week } : undefined
}
